import {
  Button,
  Carousel,
  EButtonColorVariant,
  ELinkColorVariant,
  ELinkVariant,
  Link,
} from '@outdoorsyco/bonfire';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { IFunctionResponse, useChatbot } from '@/components/chatbot/ChatbotProvider';
import { formatQueryParams, formatRentalTiles } from '@/components/chatbot/utils';
import { useBreakpointContext } from '@/components/common/BreakpointContext';
import { ListingTile } from '@/components/ui/ListingTile';
import {
  trackChatbotRefineSearchClicked,
  trackChatbotShowMoreClicked,
  trackChatbotTripPlannerRentalSearchResultsDisplayed,
} from '@/services/analytics/chatbot';
import { IRentalTile } from '@/utility/mapSearchResultToTile';

interface IRentalSearchResultProps {
  functionResponse: IFunctionResponse;
}

const RentalSearchResult = ({ functionResponse }: IRentalSearchResultProps) => {
  const intl = useIntl();
  const { isAboveTablet } = useBreakpointContext();
  const { submitMessage, trackingEventData } = useChatbot();

  const rentalSearchResultData = {
    text: functionResponse.text,
    trip_name: functionResponse.trip_name as string,
    query_params: formatQueryParams(functionResponse.query_params as string),
    tiles: formatRentalTiles(functionResponse.tiles as unknown[]),
  };

  const eventData = {
    ...trackingEventData,
    tileCount: rentalSearchResultData?.tiles?.length || 0,
    tripName: rentalSearchResultData?.trip_name,
    queryParams: rentalSearchResultData?.query_params,
  };

  useEffect(() => {
    trackChatbotTripPlannerRentalSearchResultsDisplayed(eventData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickRefineSearch = () => {
    trackChatbotRefineSearchClicked(eventData);
    const refineSearchMessage = intl.formatMessage({
      defaultMessage:
        "These don't look quite right for this trip. Can you show me some different options?",
      id: 'APVriv',
    });
    submitMessage(refineSearchMessage);
  };

  const onClickShowMore = () => {
    trackChatbotShowMoreClicked(eventData);
  };

  return (
    rentalSearchResultData?.tiles &&
    rentalSearchResultData?.tiles?.length > 0 && (
      <div className="px-4 pt-0 pb-4 rounded-lg bg-gray-10">
        <div
          className="w-full overflow-hidden"
          style={{
            maxWidth: isAboveTablet ? '636px' : 'min(700px, calc(100vw - 4rem))',
          }}>
          <Carousel
            content={rentalSearchResultData?.tiles?.map((tile: IRentalTile, index: number) => (
              <ListingTile
                key={index}
                rentalTile={tile}
                target={'_blank'}
                showQuickPreview
                customWishlistName={rentalSearchResultData.trip_name}
              />
            ))}
            title={rentalSearchResultData.trip_name}
            freeMode
            slidesPerView={1.2}
            spaceBetween={16}
          />
        </div>
        <div className="flex flex-col items-center justify-center gap-3">
          {rentalSearchResultData.query_params ? (
            <Button
              label="Show me more"
              variant={EButtonColorVariant.Tertiary}
              href={`/rv-search?${rentalSearchResultData.query_params}`}
              target="_blank"
              onClick={onClickShowMore}
            />
          ) : null}
          <Link
            onClick={onClickRefineSearch}
            label="These don't look quite right"
            variant={ELinkVariant.Underline}
            colorVariant={ELinkColorVariant.Primary}
          />
        </div>
      </div>
    )
  );
};

export default RentalSearchResult;
